import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../services/expenseVouchersApi';
import { formatMoney, WALLET_TYPE_LABELS, SETTLEMENT_RESULT_META } from '../../services/expenseAccountsApi';
import { operationsApi } from '../../services/expenseOperationsApi';
import { historyLabel, ROUTE_LABELS } from '../../components/expense/voucherShared';

// Printable expense voucher (Phase 5B-2). "Print / Save as PDF" uses the browser's print
// dialog. The server decides what this user may see (same rules as the voucher detail
// screens): Accounts get the Accounts section, the employee and approvers do not.

const SETTLED_VIA_TEXT = {
  CASH: 'CASH - paid to the employee',
  WALLET: 'WALLET - settled against the employee wallet, no payment made',
  NO_PAYMENT: 'NO_PAYMENT - nothing payable, no payment made',
};

const Field = ({ label, children }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
    <Typography variant="body2" component="div">{children || '-'}</Typography>
  </Box>
);

const Section = ({ title, children, testId }) => (
  <Box sx={{ mt: 3, breakInside: 'avoid' }} data-testid={testId}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, borderBottom: 1, borderColor: 'divider' }}>{title}</Typography>
    {children}
  </Box>
);

const cell = { py: 0.5, px: 1, fontSize: 12 };
const headCell = { ...cell, fontWeight: 700, bgcolor: 'grey.100' };

const SimpleTable = ({ head, rows, testId }) => (
  <Table size="small" data-testid={testId}>
    <TableHead>
      <TableRow>{head.map((h) => <TableCell key={h} sx={headCell}>{h}</TableCell>)}</TableRow>
    </TableHead>
    <TableBody>
      {rows.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableRow key={i}>{r.map((c, j) => <TableCell key={j} sx={cell}>{c}</TableCell>)}</TableRow>
      ))}
    </TableBody>
  </Table>
);

const ExpenseVoucherPrint = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    operationsApi.printVoucher(id)
      .then((res) => { if (alive) setData(res.data); })
      .catch((e) => { if (alive) setError(e); });
    return () => { alive = false; };
  }, [id]);

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error.status === 404 ? 'Voucher not found, or you are not allowed to view it.' : error.message}</Alert>
      </Box>
    );
  }
  if (!data) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;

  const { voucher: v, employee, lines, approval, history, settlement, completion, accounts, walletEffect, payments, walletEntries } = data;
  const cur = accounts?.currency || lines[0]?.currency || '';

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto', bgcolor: 'background.paper', '@media print': { p: 0, maxWidth: 'none' } }}>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 2, displayPrint: 'none' }}>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>Print / Save as PDF</Button>
        <Button variant="outlined" onClick={() => window.close()}>Close</Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Expense Voucher</Typography>
          <Typography variant="h6" data-testid="print-voucher-number">{v.voucherNumber}</Typography>
          {v.isSettlement && <Typography variant="body2" color="text.secondary">Settlement voucher</Typography>}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Chip label={v.statusLabel} size="small" sx={{ mb: 0.5 }} />
          {completion && (
            <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="print-completion">{completion.paymentStatusLabel}</Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Printed {formatDateTime(data.generatedAt)}</Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><Field label="Employee">{employee.name}</Field></Grid>
        <Grid item xs={6} sm={3}><Field label="Employee ID">{employee.employeeId}</Field></Grid>
        <Grid item xs={6} sm={3}><Field label="Department">{employee.department}</Field></Grid>
        <Grid item xs={6} sm={3}><Field label="Employee country">{employee.homeCountry}</Field></Grid>
        <Grid item xs={6} sm={3}><Field label="Voucher date">{formatDate(v.voucherDate)}</Field></Grid>
        <Grid item xs={6} sm={3}><Field label="Submitted">{formatDateTime(v.submittedAt)}</Field></Grid>
        <Grid item xs={6} sm={3}><Field label={v.status === 'REJECTED' ? 'Rejected' : 'Approved'}>{formatDateTime(v.status === 'REJECTED' ? v.rejectedAt : v.approvedAt)}</Field></Grid>
        <Grid item xs={6} sm={3}><Field label="Invoice no.">{v.invoiceNumber}</Field></Grid>
        {accounts && (
          <>
            <Grid item xs={6} sm={3}><Field label="Project / job">{accounts.jobName}</Field></Grid>
            <Grid item xs={6} sm={3}><Field label="Billing country">{accounts.billingCountryName}</Field></Grid>
          </>
        )}
      </Grid>

      <Section title="Expense lines" testId="print-lines">
        <SimpleTable
          head={['Date', 'Vendor', 'Category', 'Head', 'Employment / expense country', 'Payment type', 'Paid Via', 'Currency', 'Amount', 'Description', 'Bills']}
          rows={lines.map((l) => [
            formatDate(l.expenseDate), l.vendor || '-', l.category || '-', l.head || '-',
            `${l.employmentCountry || '-'} / ${l.expenseCountry || '-'}`, l.paymentType || '-', l.paidVia || '-',
            l.currency || '-', formatMoney(l.amount), l.description || '-', l.billCount,
          ])}
        />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'right', fontWeight: 600 }} data-testid="print-totals">
          Total: {v.totalsByCurrency.length ? v.totalsByCurrency.map((t) => formatMoney(t.amount, t.currency)).join(' + ') : '-'}
        </Typography>
      </Section>

      {accounts && (
        <Section title="Accounts" testId="print-accounts">
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><Field label="Requested">{formatMoney(accounts.requestedAmount, cur)}</Field></Grid>
            <Grid item xs={6} sm={3}><Field label="Cost to Project">{formatMoney(accounts.costToProject, cur)}</Field></Grid>
            <Grid item xs={6} sm={3}>
              <Field label={accounts.netPayableEstimated ? 'Net Payable (estimated)' : 'Net Payable'}>{formatMoney(accounts.netPayable, cur)}</Field>
            </Grid>
            <Grid item xs={6} sm={3}><Field label="Paid total">{formatMoney(accounts.paidTotal, cur)}</Field></Grid>
            <Grid item xs={6} sm={3}><Field label="Balance payable">{formatMoney(accounts.balancePayable, cur)}</Field></Grid>
            <Grid item xs={6} sm={3}><Field label="Payment status">{completion?.paymentStatusLabel}</Field></Grid>
            <Grid item xs={6} sm={3}><Field label="Booked">{accounts.bookedAt ? `${formatDateTime(accounts.bookedAt)} by ${accounts.bookedByName || '-'}` : '-'}</Field></Grid>
            <Grid item xs={6} sm={3}><Field label="Narration">{accounts.narration}</Field></Grid>
            {accounts.holdReason && <Grid item xs={12} sm={6}><Field label="Hold reason">{accounts.holdReason}</Field></Grid>}
            {accounts.cancelReason && <Grid item xs={12} sm={6}><Field label="Cancel reason">{accounts.cancelReason}</Field></Grid>}
          </Grid>
          {payments?.length > 0 && (
            <SimpleTable
              testId="print-payments"
              head={['Date', 'Amount', 'Status', 'Paid at', 'Comment']}
              rows={payments.map((p) => [formatDate(p.date), formatMoney(p.amount, cur), p.status, formatDateTime(p.paidAt), p.voidReason ? `${p.comment} (void: ${p.voidReason})` : p.comment || '-'])}
            />
          )}
        </Section>
      )}

      {(walletEffect || completion?.settledVia || walletEntries?.length > 0) && (
        <Section title="Wallet effect" testId="print-wallet">
          {completion?.settledVia && (
            <Typography variant="body2" sx={{ mb: 1 }} data-testid="print-settled-via">
              Settled via: <strong>{SETTLED_VIA_TEXT[completion.settledVia]}</strong>
            </Typography>
          )}
          {walletEffect && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }} data-testid="print-wallet-basis">
                {walletEffect.basis === 'POSTED'
                  ? <>Posted to the wallet on {formatDateTime(walletEffect.postedAt)}{walletEffect.reversedAt ? ` · reversed on ${formatDateTime(walletEffect.reversedAt)}` : ''}</>
                  : <>Estimated from the current wallet balance - not posted yet (posted at the first actual payment){walletEffect.postingPending ? ' · posting pending' : ''}</>}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}><Field label="Wallet before">{formatMoney(walletEffect.balanceBefore, cur)}</Field></Grid>
                <Grid item xs={6} sm={3}><Field label="Wallet adjustment to payable">{formatMoney(walletEffect.adjustment, cur)}</Field></Grid>
                <Grid item xs={6} sm={3}><Field label="Wallet after">{formatMoney(walletEffect.balanceAfter, cur)}</Field></Grid>
                <Grid item xs={6} sm={3}><Field label={walletEffect.basis === 'POSTED' ? 'Net Payable' : 'Net Payable (estimated)'}>{formatMoney(walletEffect.netPayable, cur)}</Field></Grid>
              </Grid>
              {walletEffect.entries?.length > 0 && (
                <SimpleTable
                  head={['Type', 'Requested', 'Credited', 'Wallet movement', 'Before', 'After', '']}
                  rows={walletEffect.entries.map((e) => [
                    WALLET_TYPE_LABELS[e.type] || e.type, formatMoney(e.requested), formatMoney(e.credited), formatMoney(e.movement),
                    formatMoney(e.balanceBefore), formatMoney(e.balanceAfter), e.reversed ? 'Reversed' : '',
                  ])}
                />
              )}
            </>
          )}
          {walletEntries?.length > 0 && (
            <SimpleTable
              testId="print-wallet-entries"
              head={['Date', 'Type', 'Wallet movement (posted)', 'Wallet after']}
              rows={walletEntries.map((e) => [formatDateTime(e.at), WALLET_TYPE_LABELS[e.type] || e.type, formatMoney(e.amount, e.currency), formatMoney(e.balanceAfter, e.currency)])}
            />
          )}
        </Section>
      )}

      {settlement && (
        <Section title="Settlement" testId="print-settlement">
          <Typography variant="body2">
            Settles advance <strong>{settlement.advanceVoucherNumber}</strong>
            {settlement.result === 'CURRENCY_MISMATCH'
              ? ` - currency mismatch (${settlement.advanceCurrency} advance, ${settlement.currency} settlement)`
              : ` - advance ${formatMoney(settlement.advanceAmount, settlement.currency)}, settlement ${formatMoney(settlement.settlementAmount, settlement.currency)}, ${SETTLEMENT_RESULT_META[settlement.result]?.label || settlement.result}`}
          </Typography>
        </Section>
      )}

      <Section title="Approvals" testId="print-approvals">
        {approval.routing && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Routed by {approval.routing.byName || 'Accounts'} on {formatDateTime(approval.routing.at)}: {ROUTE_LABELS[approval.routing.option] || approval.routing.option}
          </Typography>
        )}
        {approval.groups.length > 0 ? (
          <SimpleTable
            head={['Group', 'Approvers', 'Approved by', 'Date', 'Note']}
            rows={approval.groups.map((g) => [
              g.label, g.approvers.join(', ') || '-', g.approvedBy?.name || (g.pending ? 'Pending' : '-'),
              formatDateTime(g.approvedBy?.at), g.approvedBy?.note || '-',
            ])}
          />
        ) : <Typography variant="body2" color="text.secondary">No approver routing recorded.</Typography>}
      </Section>

      <Section title="History" testId="print-history">
        <SimpleTable
          head={['Date', 'Action', 'By', 'Comment']}
          rows={history.map((h) => [
            formatDateTime(h.at),
            `${historyLabel(h)}${typeof h.amount === 'number' ? ` - ${formatMoney(h.amount, h.currency)}` : ''}`,
            h.byName || '-',
            h.comment || '-',
          ])}
        />
      </Section>
    </Box>
  );
};

export default ExpenseVoucherPrint;
